import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import {
  useSlaCompliance,
  useTurnaround,
  useAtRiskHolds,
  useWorkload,
  useChartingEfficiency,
  useBacklogTrend,
} from '@/hooks/useAnalytics';
import { KpiCard } from '@/components/analytics/KpiCard';
import { DrillDownTable, type ColumnDef } from '@/components/analytics/DrillDownTable';
import { ChartCard } from '@/components/analytics/ChartCard';
import { LineChartWrapper } from '@/components/analytics/LineChartWrapper';
import { BarChartWrapper } from '@/components/analytics/BarChartWrapper';
import { AreaChartWrapper } from '@/components/analytics/AreaChartWrapper';
import type {
  AnalyticsFilters,
  NonCompliantItem,
  AtRiskItem,
  EfficiencyMember,
} from '@/types';

interface AdOpsViewProps {
  filters: AnalyticsFilters;
}

/** Section heading style per OUTFRONT design system */
const SECTION_HEADING = 'text-[16px] font-semibold uppercase tracking-[0.05em] text-[#111111]';

/** SLA color coding thresholds */
const SLA_THRESHOLDS = { green: 90, yellow: 80 };

// ─── Section 1: SLA Compliance ──────────────────────────────────────────────

function SlaComplianceSection({ filters }: { filters: AnalyticsFilters }) {
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('daysOverdue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const pagination = { page, pageSize, sortBy, sortDir };
  const { data, isLoading, error } = useSlaCompliance(filters, showDrillDown ? pagination : undefined);

  const handleSort = (newSortBy: string, newSortDir: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    setPage(1);
  };

  const columns: ColumnDef<NonCompliantItem>[] = [
    { key: 'requestNumber', header: 'Request #', sortable: true },
    { key: 'displayName', header: 'Display Name', sortable: true },
    { key: 'chartingMemberName', header: 'Charting Member', sortable: true, render: (row) => row.chartingMemberName ?? '—' },
    { key: 'daysOverdue', header: 'Days Overdue', sortable: true, align: 'right' },
    { key: 'region', header: 'Region', sortable: true },
  ];

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>SLA Compliance</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          label="SLA Compliance Rate"
          value={data?.rate != null ? data.rate.toFixed(1) : null}
          unit="%"
          trend={data?.trend ?? null}
          colorCoding={SLA_THRESHOLDS}
          colorCodingValue={data?.rate ?? null}
          loading={isLoading}
          error={error ? 'Failed to load SLA data' : null}
          onClick={() => setShowDrillDown((prev) => !prev)}
        />
      </div>
      {showDrillDown && data && (
        <div className="mt-4">
          <DrillDownTable<NonCompliantItem>
            columns={columns}
            data={data.nonCompliantItems.items}
            totalCount={data.nonCompliantItems.totalCount}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onPageChange={setPage}
            loading={isLoading}
            rowKey={(row) => row.id}
            emptyMessage="No non-compliant items found"
          />
        </div>
      )}
    </section>
  );
}

// ─── Section 2: Turnaround Time ─────────────────────────────────────────────

function TurnaroundSection({ filters }: { filters: AnalyticsFilters }) {
  const { data, isLoading, error } = useTurnaround(filters);

  const abandonmentBadgeClass = (() => {
    if (data?.abandonmentRate == null) return 'bg-[#EFEFEF] text-[#555555]';
    if (data.abandonmentRate > 10) return 'bg-[#FEE2E2] text-[#B91C1C]';
    if (data.abandonmentRate > 5) return 'bg-[#FEF3C7] text-[#92400E]';
    return 'bg-[#E8F5ED] text-[#1A7A3C]';
  })();

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Turnaround Time</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          label="Mean Turnaround"
          value={data?.meanBusinessDays != null ? data.meanBusinessDays.toFixed(1) : null}
          unit="days"
          trend={data?.trend ?? null}
          loading={isLoading}
          error={error ? 'Failed to load turnaround data' : null}
        />
        <KpiCard
          label="Median Turnaround"
          value={data?.medianBusinessDays != null ? data.medianBusinessDays.toFixed(1) : null}
          unit="days"
          loading={isLoading}
          error={error ? 'Failed to load turnaround data' : null}
        />
        <div className="flex items-center">
          {isLoading ? (
            <div className="animate-pulse bg-neutral-200 rounded h-6 w-32" />
          ) : error ? (
            <span className="text-sm text-[#B91C1C]">Error</span>
          ) : (
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-[0.05em]',
                abandonmentBadgeClass
              )}
            >
              Abandonment: {data?.abandonmentRate != null ? `${data.abandonmentRate.toFixed(1)}%` : '—'}
            </span>
          )}
        </div>
      </div>
      <div className="mt-6">
        <ChartCard
          title="7-Day Rolling Average Turnaround"
          loading={isLoading}
          error={error ? 'Failed to load turnaround trend' : null}
        >
          {data?.dailyAverage && data.dailyAverage.length > 0 ? (
            <LineChartWrapper
              data={data.dailyAverage as Record<string, unknown>[]}
              xAxisKey="date"
              series={[
                { dataKey: 'avgBusinessDays', name: 'Avg Business Days', color: '#6B4FBB' },
              ]}
              yAxisLabel="Business Days"
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No trend data available</p>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
}

// ─── Section 3: At-Risk / Overdue ───────────────────────────────────────────

function AtRiskOverdueSection({ filters }: { filters: AnalyticsFilters }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('daysUntilExpiration');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const pagination = { page, pageSize, sortBy, sortDir };
  const { data, isLoading, error } = useAtRiskHolds(filters, pagination);

  const handleSort = (newSortBy: string, newSortDir: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    setPage(1);
  };

  const columns: ColumnDef<AtRiskItem>[] = [
    { key: 'requestNumber', header: 'Request #', sortable: true },
    { key: 'displayName', header: 'Display Name', sortable: true },
    { key: 'salesAeName', header: 'Sales AE', sortable: true },
    { key: 'chartingMemberName', header: 'Charting Member', sortable: true, render: (row) => row.chartingMemberName ?? '—' },
    { key: 'region', header: 'Region', sortable: true },
    { key: 'holdExpirationDate', header: 'Expiration Date', sortable: true, render: (row) => formatDate(row.holdExpirationDate) },
    {
      key: 'daysUntilExpiration',
      header: 'Days Until/Past Expiration',
      sortable: true,
      align: 'right',
      render: (row) => {
        const val = row.daysUntilExpiration;
        if (val < 0) return <span className="text-[#B91C1C]">{val}d (overdue)</span>;
        return <span className="text-[#92400E]">{val}d</span>;
      },
    },
    { key: 'chartingStatus', header: 'Status', sortable: true },
  ];

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>At-Risk / Overdue</h2>
      <div className="mt-4 flex items-center gap-3">
        {isLoading ? (
          <>
            <div className="animate-pulse bg-neutral-200 rounded h-6 w-24" />
            <div className="animate-pulse bg-neutral-200 rounded h-6 w-24" />
          </>
        ) : error ? (
          <span className="text-sm text-[#B91C1C]">Failed to load at-risk data</span>
        ) : (
          <>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-[0.05em] bg-[#FEE2E2] text-[#B91C1C]">
              Overdue: {data?.overdueCount ?? 0}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-[0.05em] bg-[#FEF3C7] text-[#92400E]">
              At-Risk: {data?.atRiskCount ?? 0}
            </span>
          </>
        )}
      </div>
      <div className="mt-4">
        <DrillDownTable<AtRiskItem>
          columns={columns}
          data={data?.items.items ?? []}
          totalCount={data?.items.totalCount ?? 0}
          page={page}
          pageSize={pageSize}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          onPageChange={setPage}
          loading={isLoading}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/holds/${row.holdRequestId}`)}
          emptyMessage="No at-risk or overdue items"
        />
      </div>
    </section>
  );
}

// ─── Section 4: Workload Distribution ───────────────────────────────────────

function WorkloadSection({ filters }: { filters: AnalyticsFilters }) {
  const { data, isLoading, error } = useWorkload(filters);

  // Build chart data: one bar per member + unassigned
  const chartData = (() => {
    if (!data) return [];
    const overloadThreshold = data.teamAverage * 1.5;
    const memberBars = data.members.map((m) => ({
      name: m.name,
      Active: m.activeCount,
      'Exception Pending': m.exceptionPendingCount,
      'Revision Pending': m.revisionPendingCount,
      _overloaded: m.totalActive > overloadThreshold,
    }));
    memberBars.push({
      name: 'Unassigned',
      Active: data.unassignedCount,
      'Exception Pending': 0,
      'Revision Pending': 0,
      _overloaded: false,
    });
    return memberBars;
  })();

  // Identify overloaded members for display
  const overloadedMembers = data?.members.filter(
    (m) => m.totalActive > (data.teamAverage * 1.5)
  ) ?? [];

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Workload Distribution</h2>
      <div className="mt-4">
        <ChartCard
          title="Active Items by Charting Member"
          loading={isLoading}
          error={error ? 'Failed to load workload data' : null}
          footer={
            data ? (
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#555555]">
                <span>Team Average: <strong>{data.teamAverage.toFixed(1)}</strong></span>
                {overloadedMembers.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[#92400E]">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                    {overloadedMembers.length} member{overloadedMembers.length > 1 ? 's' : ''} overloaded
                  </span>
                )}
              </div>
            ) : undefined
          }
        >
          {chartData.length > 0 ? (
            <BarChartWrapper
              data={chartData as Record<string, unknown>[]}
              categoryKey="name"
              horizontal
              height={Math.max(300, chartData.length * 40)}
              series={[
                { dataKey: 'Active', name: 'Active', color: '#6B4FBB', stackId: 'workload' },
                { dataKey: 'Exception Pending', name: 'Exception Pending', color: '#92400E', stackId: 'workload' },
                { dataKey: 'Revision Pending', name: 'Revision Pending', color: '#B91C1C', stackId: 'workload' },
              ]}
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No workload data available</p>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
}

// ─── Section 5: Charting Efficiency ─────────────────────────────────────────

function ChartingEfficiencySection({ filters }: { filters: AnalyticsFilters }) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('completedCount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const pagination = { page, pageSize, sortBy, sortDir };
  const { data, isLoading, error } = useChartingEfficiency(filters, pagination);

  const handleSort = (newSortBy: string, newSortDir: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    setPage(1);
  };

  const columns: ColumnDef<EfficiencyMember>[] = [
    { key: 'name', header: 'Charting Member', sortable: true },
    { key: 'completedCount', header: 'Items Completed', sortable: true, align: 'right' },
    {
      key: 'avgCompletionBusinessDays',
      header: 'Avg Completion Time',
      sortable: true,
      align: 'right',
      render: (row) => row.avgCompletionBusinessDays != null ? `${row.avgCompletionBusinessDays.toFixed(1)} days` : '—',
    },
    {
      key: 'slaComplianceRate',
      header: 'SLA Compliance %',
      sortable: true,
      align: 'right',
      render: (row) => row.slaComplianceRate != null ? `${row.slaComplianceRate.toFixed(1)}%` : '—',
    },
  ];

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Charting Efficiency</h2>
      {error ? (
        <div className="mt-4 text-sm text-[#B91C1C]">Failed to load charting efficiency data</div>
      ) : (
        <div className="mt-4">
          <DrillDownTable<EfficiencyMember>
            columns={columns}
            data={data?.members.items ?? []}
            totalCount={data?.members.totalCount ?? 0}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onPageChange={setPage}
            loading={isLoading}
            rowKey={(row) => row.userId}
            emptyMessage="No charting efficiency data available"
          />
        </div>
      )}
    </section>
  );
}

// ─── Section 6: Backlog Trend ───────────────────────────────────────────────

function BacklogTrendSection({ filters }: { filters: AnalyticsFilters }) {
  const { data, isLoading, error } = useBacklogTrend(filters);

  return (
    <section className="mb-8">
      <h2 className={SECTION_HEADING}>Backlog Trend</h2>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          label="Current Unassigned"
          value={data?.currentCount ?? null}
          loading={isLoading}
          error={error ? 'Failed to load backlog data' : null}
        />
      </div>
      <div className="mt-6">
        <ChartCard
          title="Unassigned Backlog Over Time"
          loading={isLoading}
          error={error ? 'Failed to load backlog trend' : null}
        >
          {data?.snapshots && data.snapshots.length > 0 ? (
            <AreaChartWrapper
              data={data.snapshots as Record<string, unknown>[]}
              xAxisKey="date"
              series={[
                { dataKey: 'unassignedCount', name: 'Unassigned', color: '#6B4FBB' },
              ]}
              yAxisLabel="Count"
              yDomain={[0, 'auto']}
            />
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm text-[#555555]">No backlog trend data available</p>
            </div>
          )}
        </ChartCard>
      </div>
    </section>
  );
}

// ─── Main AdOps View ────────────────────────────────────────────────────────

/**
 * AdOps View — displays all 6 operational metric sections.
 * Each section has independent loading and error handling.
 */
export function AdOpsView({ filters }: AdOpsViewProps) {
  return (
    <div>
      <SlaComplianceSection filters={filters} />
      <TurnaroundSection filters={filters} />
      <AtRiskOverdueSection filters={filters} />
      <WorkloadSection filters={filters} />
      <ChartingEfficiencySection filters={filters} />
      <BacklogTrendSection filters={filters} />
    </div>
  );
}

export default AdOpsView;
